<script setup lang="ts">
import { computed, type ComputedRef, onMounted, type Ref, ref } from 'vue';
import {
  RobotVisualizer,
  PoseConfig,
  RobotJointAnglesConfig,
  RobotModulesConfig,
  RobotModuleDatabase,
  getFileFromUrl,
  fixClamps,
  nameOf,
  type ShiftVector,
  type PoseLabels,
} from '@therobotcompany/robot-visualizer';
import { ENVIRONMENTS_BASE_URL, MODULES_BASE_URL } from '@/Constants';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

let moduleDb = new RobotModuleDatabase('');

const openPanel = ref();

const showSettings = ref(true);
const isRobotLoaded = ref(false);
const showGrid = ref(false);
const showAxesHelper = ref(true);
const uploadEnvironmentFile: Ref<File | undefined> = ref(undefined);
const environmentFileOrUrl: Ref<File | string | undefined> = ref(undefined);
const robotShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentShift: Ref<ShiftVector> = ref([0, 0, 0, 0, 0, 0]);
const environmentScale: Ref<number> = ref(1);

// Default robot if none is provided via url parameters
const jointAngles: Ref<number[]> = ref([180, 30, -120, 0, -30, 0]);
// v3 Mighty Martha
const moduleIds: Ref<string[]> = ref([
  '0305',
  '0302',
  '0326',
  '0301',
  '0301',
  '0315',
  '0300',
  '0300',
  '1008',
]);

const poseLabels: ComputedRef<PoseLabels> = computed(() => [
  $gettext('Position X'),
  $gettext('Position Y'),
  $gettext('Position Z'),
  $gettext('Rotation Z'),
  $gettext('Rotation Y'),
  $gettext('Rotation X'),
]);

function toggleSettings() {
  showSettings.value = !showSettings.value;
}

function changeEnvFile() {
  environmentFileOrUrl.value = uploadEnvironmentFile.value;
}

function loadDemoEnvironment(filename: string) {
  getFileFromUrl(`${ENVIRONMENTS_BASE_URL}/${filename}`, filename).then(
    (file: File) => {
      environmentFileOrUrl.value = file;
    },
  );
}

function clearEnvFile() {
  environmentFileOrUrl.value = undefined;
  uploadEnvironmentFile.value = undefined;
}

function parseUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlModuleList = urlParams.get('modules');
  if (urlModuleList != null) {
    if (urlModuleList === '') {
      moduleIds.value = [];
    } else {
      moduleIds.value = urlModuleList
        .split(',')
        .map((e) => parseInt(e, 10).toString().padStart(4, '0'));
    }
  }
  const urlAngleList = urlParams.get('angles');
  if (urlAngleList != null) {
    if (urlAngleList === '') {
      jointAngles.value = [];
    } else {
      jointAngles.value = urlAngleList.split(',').map((e) => parseFloat(e));
    }
  }
}

function updateUrl() {
  const moduleIdsNoClamps = moduleIds.value
    .filter((moduleId: string) => !nameOf(moduleDb, moduleId).startsWith('C'))
    .map(Number);
  const search = `?modules=${moduleIdsNoClamps}&angles=${jointAngles.value}`;

  if (search !== window.location.search) {
    const url = `${location.origin + location.pathname}${search}`;

    if (window.location.search === '') {
      history.replaceState({}, '', url);
    } else {
      history.pushState({}, '', url);
    }
  }
}

window.addEventListener('popstate', parseUrl);

parseUrl();
updateUrl();

function onEnvironmentLoadingError(filename: string) {
  alert(
    `Could not load environment file '${filename}'. Please provide a valid .gltf/.glb/.obj/.stl file.`,
  );
  environmentFileOrUrl.value = undefined;
}
function onRobotLoadedChanged(robotLoaded: boolean) {
  isRobotLoaded.value = robotLoaded;
  if (robotLoaded) {
    updateUrl();
  }
}

const moduleSelection: { title: string; value: string }[] = [];
function onModuleDbLoaded(newModuleDb: RobotModuleDatabase) {
  moduleDb = newModuleDb;
  const mapping = moduleDb.moduleFolderMapping;
  moduleSelection.length = 0;
  Object.keys(mapping).forEach((key) => {
    const module = mapping[key];
    if (module.isLatestModuleOfName && key.startsWith('0')) {
      moduleSelection.push({ title: module.moduleName, value: key });
    }
  });
  moduleIds.value = fixClamps(moduleDb, moduleIds.value);
}

onMounted(() => {
  if (window.innerWidth < 1024 || window.innerHeight < 768) {
    // initially hide settings on mobile
    showSettings.value = false;
  }
});
</script>

<template>
  <div class="robot-visualizer-container">
    <robot-visualizer
      ref="visualizer"
      v-model:environmentScale="environmentScale"
      :modulesBaseUrl="MODULES_BASE_URL"
      :moduleIds="moduleIds"
      :currentAngles="jointAngles"
      :showGrid="showGrid"
      :showAxesHelper="showAxesHelper"
      :tcpShift="[0, 0, 0, 0, 0, 0]"
      :showSettings="showSettings"
      :environmentShift="environmentShift"
      :robotShift="robotShift"
      backgroundColor="#FCF9F7"
      :environmentFileOrUrl="environmentFileOrUrl"
      @onEnvironmentLoadingError="onEnvironmentLoadingError"
      @onRobotLoadedChanged="onRobotLoadedChanged"
      @onModuleDbLoaded="onModuleDbLoaded"
    />

    <div class="settings-container">
      <v-btn
        v-if="isRobotLoaded"
        class="settings-button gradient-oceanblue-whitesands"
        icon
        color="sunrise-apricot"
        @click="toggleSettings"
      >
        <v-icon icon="mdi-tune" />
      </v-btn>
      <v-card
        v-if="isRobotLoaded && showSettings"
        class="settings-panel pa-4 mt-2"
      >
        <v-switch
          v-model="showAxesHelper"
          class="ml-2"
          :label="$gettext('Show Coordinate Systems')"
          color="primary"
          hide-details
          density="compact"
        />
        <v-switch
          v-model="showGrid"
          class="mt-2 ml-2"
          :label="$gettext('Show Grid')"
          color="primary"
          hide-details
          density="compact"
        />
        <v-expansion-panels
          v-model="openPanel"
          variant="accordion"
          class="mt-4"
        >
          <v-expansion-panel>
            <v-expansion-panel-title>{{
              $gettext('Robot Pose')
            }}</v-expansion-panel-title>
            <v-expansion-panel-text>
              <pose-config
                v-model:shift="robotShift"
                class="mt-6"
                :disableScale="true"
                :pose-labels="poseLabels"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title>{{
              $gettext('Environment')
            }}</v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-file-input
                v-model="uploadEnvironmentFile"
                class="environment-file-upload my-4"
                :label="$gettext('Environment')"
                variant="underlined"
                density="compact"
                :hint="$gettext('Supported types: .gltf, .glb, .obj, .stl')"
                accept=".gltf, .glb, .obj, .stl"
                @change="changeEnvFile"
                @click:clear="clearEnvFile"
                ><template #append>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        icon="mdi-export"
                        v-bind="props"
                        @click="
                          (
                            $refs.visualizer as typeof RobotVisualizer
                          ).exportScene()
                        "
                      />
                    </template>
                    {{ $gettext('Export scene') }}
                  </v-tooltip>
                </template>
              </v-file-input>
              <pose-config
                v-if="environmentFileOrUrl"
                v-model:shift="environmentShift"
                v-model:scale="environmentScale"
                :pose-labels="poseLabels"
              />
              <v-btn
                class="w-100"
                @click="loadDemoEnvironment('demo_environment_palletizing.glb')"
              >
                {{ $gettext('Palletizing demo') }}
              </v-btn>
              <v-btn
                class="w-100 mt-3"
                @click="
                  loadDemoEnvironment('demo_environment_machine_tending.glb')
                "
              >
                {{ $gettext('Machine Tending demo') }}
              </v-btn>
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title>{{
              $gettext('Joint Angles')
            }}</v-expansion-panel-title>
            <v-expansion-panel-text>
              <robot-joint-angles-config
                v-model:jointAngles="jointAngles"
                class="mt-4"
                @update:jointAngles="updateUrl"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-title>{{
              $gettext('Robot Modules')
            }}</v-expansion-panel-title>
            <v-expansion-panel-text>
              <robot-modules-config
                v-model:moduleIds="moduleIds"
                v-model:jointAngles="jointAngles"
                class="mt-4"
                :moduleTitle="$gettext('Module')"
                :moduleDb="moduleDb"
                :moduleSelection="moduleSelection"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </div>
  </div>
</template>

<style lang="scss">
.robot-visualizer-container {
  position: relative;
  width: 100%;
  height: 100%;

  canvas {
    border-radius: 0 !important;
  }
}

.settings-container {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 2051;

  .settings-panel {
    overflow: auto;
    max-height: calc(100vh - 150px);
    width: 350px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .settings-button {
    display: block;
    margin-left: auto;
    margin-right: 0;
  }

  .environment-file-upload {
    .v-field__input {
      overflow-wrap: break-word;
      display: inline;
    }
  }

  .close-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
  }
}
</style>
